import { useEffect, useState } from "react";

// Shared thumb styling for both browser engines — Tailwind's arbitrary
// variants target the vendor pseudo-elements directly so no separate
// CSS file is needed just for this one control.
const THUMB_CLASSES =
  "pointer-events-none absolute inset-x-0 top-1/2 h-4 w-full -translate-y-1/2 appearance-none bg-transparent " +
  "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 " +
  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 " +
  "[&::-webkit-slider-thumb]:border-gold-500 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md " +
  "[&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing " +
  "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 " +
  "[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 " +
  "[&::-moz-range-thumb]:border-gold-500 [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-md " +
  "[&::-moz-range-thumb]:cursor-grab";

// Dual-handle price slider. Dragging feels instant (local state updates
// every input event) but the parent — and therefore the network
// request — only hears about it once the user lets go, via onChangeEnd.
// That keeps a drag from firing a product refetch per pixel moved.
export default function PriceRangeSlider({ min, max, value, onChangeEnd, formatValue = (v) => `$${v}` }) {
  const [draft, setDraft] = useState(value ?? [min, max]);

  useEffect(() => {
    setDraft(value ?? [min, max]);
  }, [value, min, max]);

  const bounded = max > min;
  const [lo, hi] = draft;
  const loPct = bounded ? ((lo - min) / (max - min)) * 100 : 0;
  const hiPct = bounded ? ((hi - min) / (max - min)) * 100 : 100;

  function commit(next) {
    setDraft(next);
    onChangeEnd(next);
  }

  function handleLoInput(raw) {
    const next = Math.min(Number(raw), hi);
    setDraft([next, hi]);
  }

  function handleHiInput(raw) {
    const next = Math.max(Number(raw), lo);
    setDraft([lo, next]);
  }

  if (!bounded) return null;

  return (
    <div className="w-full max-w-xs">
      <div className="mb-2 flex items-center justify-between text-xs font-medium text-ink">
        <span>{formatValue(lo)}</span>
        <span>{formatValue(hi)}</span>
      </div>
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-mono/10" />
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-gold-500"
          style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={lo}
          onChange={(e) => handleLoInput(e.target.value)}
          onMouseUp={() => commit(draft)}
          onTouchEnd={() => commit(draft)}
          onKeyUp={() => commit(draft)}
          aria-label="Minimum price"
          className={THUMB_CLASSES}
          style={{ zIndex: lo === max ? 4 : 3 }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={hi}
          onChange={(e) => handleHiInput(e.target.value)}
          onMouseUp={() => commit(draft)}
          onTouchEnd={() => commit(draft)}
          onKeyUp={() => commit(draft)}
          aria-label="Maximum price"
          className={THUMB_CLASSES}
          style={{ zIndex: 3 }}
        />
      </div>
    </div>
  );
}
